/**
 * V2 Block Helpers
 *
 * Helper functions for creating blocks and documents in V2 schema.
 * Uses the block registry for default content, ensuring consistency.
 */

import type { Block, BlockType } from "./blocks";
import type { CanvasDocumentContentV2 } from "./schema";
import { generateBlockId } from "@/canvas/utils/id";
import { getBlockConfig } from "@/canvas/registry";

/**
 * Type-safe content lookup by block type.
 * Extracts the content type from a specific block type.
 *
 * @example
 * type RisksContent = ContentForType<"risks">;
 * // { items: Risk[] }
 */
export type ContentForType<T extends BlockType> = Extract<Block, { type: T }>["content"];

/**
 * Create a new block with a generated ID.
 * Uses the registry's defaultContent for consistency.
 *
 * @param type - The block type to create
 * @param order - The display order for the block
 * @param overrides - Optional partial block to merge (excluding id and type)
 * @returns A new block of the specified type with generated ID
 *
 * @example
 * // Create an empty risks block
 * const risks = createBlock("risks", 0);
 *
 * // Create a markdown block with content
 * const md = createBlock("markdown", 1, {
 *   content: { text: "# Hello World" },
 *   title: "Introduction",
 * });
 */
export function createBlock<T extends BlockType>(
  type: T,
  order: number,
  overrides?: Partial<Omit<Extract<Block, { type: T }>, "id" | "type">>,
): Extract<Block, { type: T }> {
  const config = getBlockConfig(type);
  if (!config) {
    throw new Error(`Unknown block type: ${type}`);
  }

  return {
    id: generateBlockId(),
    type,
    order,
    content: config.defaultContent(),
    ...overrides,
  } as Extract<Block, { type: T }>;
}

/**
 * Create an empty V2 document content structure.
 * Use this as the starting point for new documents.
 *
 * @returns Empty CanvasDocumentContentV2 with schemaVersion: 2
 *
 * @example
 * const newDoc = createEmptyDocumentContent();
 * newDoc.blocks.push(createBlock("markdown", 0, {
 *   content: { text: "# My Report" },
 * }));
 */
export function createEmptyDocumentContent(): CanvasDocumentContentV2 {
  return {
    schemaVersion: 2,
    blocks: [],
  };
}

/**
 * Safely extract markdown text from block content.
 *
 * Handles both V2 format (content.text) and malformed data where content
 * may be a string directly. This provides defensive handling for data
 * inconsistencies that can cause runtime crashes.
 *
 * @param content - Block content from any source/version
 * @returns The markdown text, or empty string if not found
 *
 * @example
 * // V2 format (correct)
 * getMarkdownText({ text: "# Hello" }); // "# Hello"
 *
 * // Malformed data (string directly)
 * getMarkdownText("# Hello"); // "# Hello"
 *
 * // Missing content
 * getMarkdownText(undefined); // ""
 */
type MarkdownTextContainer = {
  text?: string;
  type?: string;
};

export function getMarkdownText(
  content: MarkdownTextContainer | string | null | undefined,
): string {
  if (!content) return "";
  if (typeof content === "string") return content;
  return typeof content.text === "string" ? content.text : "";
}
